import { inject, observer } from 'mobx-react'
import { Fragment } from 'react'
import { useTheme } from '@emotion/react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'

import { withRouterProps } from '../../../utils'
import { SVGWrapper, HelpIcon, TooltipText } from '../../../components'
import { Lightbulb, SmileyFace } from '../../../assets/icons'
import { planTypeIdToAccountKey, institutionalTypeToPathKey } from '../../../constants/plan-types'
import Button from '../../../components/Button'
import { ActionItemsBox, ActionItemRow, ActionItemText, BoldText } from './styles'

const ItemRow = ({ children }) => {
  const theme = useTheme()
  return (
    <ActionItemRow>
      <SVGWrapper svg={Lightbulb} fill={theme.primaryColor} size='custom30' />
      {children}
    </ActionItemRow>
  )
}

/*
 * The alerts box. Which alerts are active is decided by `useActionItems` in
 * HomeAlerts and handed down, because the AI chatbot's opening comment is built
 * from the same keys and the two must not resolve them separately (see
 * HomeAlerts). HomeAlerts fetches the contribution limits this interpolates for
 * the same reason — the chatbot needs them on a path where this never mounts.
 */
const ActionItems = ({
  navigate,
  main,
  activeKeys,
  store: {
    standardLimit,
    standardCatchup,
    additionalCatchup,
    institutionalAccount,
    institutional,
    config: { template },
  },
}) => {
  const goToAddAccount = () => navigate('/accounts/add')

  const goToProfile = () => navigate('/profile')

  const reviewInstitutionalAccount = () => {
    main?.setSidebar(false)
    navigate(
      `/accounts/add/${institutionalTypeToPathKey[institutionalAccount.type]}/${planTypeIdToAccountKey[institutional.planType]}/intro`,
      { state: { id: institutionalAccount.id } }
    )
  }

  const currentYear = dayjs().year()

  const renderers = {
    'all clear': () => (
      <ActionItemRow>
        <SVGWrapper svg={SmileyFace} size='large' />
        <ActionItemText>All clear!</ActionItemText>
      </ActionItemRow>
    ),
    'add at least one account': () => (
      <ItemRow>
        <ActionItemText>
          <BoldText>CRITICAL:</BoldText> Add at least one account intended for living expenses in
          retirement before beginning to plan.
        </ActionItemText>
        <Button variant='tertiary' size='xsmall' type='button' onClick={goToAddAccount}>
          Add account now
        </Button>
      </ItemRow>
    ),
    'reviewIncomeDefaultSalary': () => (
      <ItemRow>
        <ActionItemText>
          <BoldText>TO-DO:</BoldText> Review your income. We may use $10,000 as a default if your
          retirement plan provider doesn’t supply your salary.&nbsp;
          <HelpIcon size='mediumLarge' tooltip={TooltipText.defaultSalaryPlaceholder()} />
        </ActionItemText>
        <Button variant='tertiary' size='xsmall' type='button' onClick={goToProfile}>
          Go to profile
        </Button>
      </ItemRow>
    ),
    'ageEqualOrGreaterThanRetirement': () => (
      <ItemRow>
        <ActionItemText>
          Looks like you've reached your stated retirement age-Congratulations! Check your next
          steps:&nbsp;
          <HelpIcon size='mediumLarge' tooltip={TooltipText.areYouRetired()} />
        </ActionItemText>
      </ItemRow>
    ),
    'spouseReachedRetirementAge': () => (
      <ItemRow>
        <ActionItemText>
          It appears your spouse has reached retirement age. We suggest reviewing your income
          goal.&nbsp;
          <HelpIcon size='mediumLarge' tooltip={TooltipText.spouseReachedRetirementAge()} />
        </ActionItemText>
      </ItemRow>
    ),
    'lumpSumPensionPayoutExpected': () => (
      <ItemRow>
        <ActionItemText>
          Have you received the lump sum pension payout yet? Our projections assume you invest it
          for future retirement expenses. Update your payout status.&nbsp;
          <HelpIcon size='mediumLarge' tooltip={TooltipText.lumpSumPensionPayout()} />
        </ActionItemText>
      </ItemRow>
    ),
    'lumpSumPensionMayBeEligible': () => (
      <ItemRow>
        <ActionItemText>
          Your plan allows a lump sum pension payout and based on your age, you may be eligible to
          receive a payment. If you’ve already received a lump sum payment, please update the payout
          status. If you have invested the funds elsewhere, please update your investments.&nbsp;
          <HelpIcon size='mediumLarge' tooltip={TooltipText.lumpSumPensionPayout()} />
        </ActionItemText>
      </ItemRow>
    ),
    'reviewRetirementAccountThatWasAddedByInstitution': () => (
      <ItemRow>
        <ActionItemText>
          {template.sponsorName} added your current retirement plan information. Please review and
          complete any missing data.
        </ActionItemText>
        <Button variant='tertiary' size='xsmall' type='button' onClick={reviewInstitutionalAccount}>
          Review account
        </Button>
      </ItemRow>
    ),
    'catchUp50to59': () => (
      <ItemRow>
        <ActionItemText>
          You have the chance to save a little more: The IRS permits those aged 50+ in the current
          calendar year to make catch-up contributions to their company’s retirement plan. The
          standard contribution limit in {currentYear} is ${standardLimit?.toLocaleString()} and you
          may contribute up to an additional ${standardCatchup?.maximumAmount?.toLocaleString()} in
          catch-up contributions for a retirement plan account maximum contribution of $
          {(standardLimit + standardCatchup?.maximumAmount).toLocaleString()}.
        </ActionItemText>
      </ItemRow>
    ),
    'catchUp60to63': () => (
      <ItemRow>
        <ActionItemText>
          You have the chance to save a little more: The IRS permits those aged 60-63 in the current
          calendar year to make a higher catch-up contribution to their company’s retirement plan.
          The standard contribution limit in {currentYear} is ${standardLimit?.toLocaleString()} and
          the standard catch-up contribution limit is $
          {standardCatchup?.maximumAmount?.toLocaleString()}. With the higher catch-up contribution
          limit, you may contribute up to an additional $
          {(additionalCatchup?.maximumAmount - standardCatchup?.maximumAmount).toLocaleString()} for
          a total catch-up contribution of ${additionalCatchup?.maximumAmount?.toLocaleString()},
          making the retirement plan account maximum contribution $
          {(standardLimit + additionalCatchup?.maximumAmount)?.toLocaleString()}.
        </ActionItemText>
      </ItemRow>
    ),
    'catchUpOver64': () => (
      <ItemRow>
        <ActionItemText>
          <BoldText>Be aware:</BoldText> You may need to reduce your retirement plan contributions.
          The IRS permits those aged 64+ in the current calendar year to make catch-up contributions
          of up to the regular catch-up contribution limit of $
          {standardCatchup?.maximumAmount?.toLocaleString()} for {currentYear}. With the standard
          contribution limit of ${standardLimit?.toLocaleString()}, this means the maximum
          contribution you can make to your company’s retirement plan is $
          {(standardLimit + standardCatchup?.maximumAmount).toLocaleString()}.
        </ActionItemText>
      </ItemRow>
    ),
  }

  return (
    <ActionItemsBox>
      {activeKeys.map(key => {
        const render = renderers[key]
        return render ? <Fragment key={key}>{render()}</Fragment> : null
      })}
    </ActionItemsBox>
  )
}

ActionItems.propTypes = {
  /** Active alert keys in display order — useActionItems' return value. */
  activeKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export default withRouterProps(inject('store', 'main')(observer(ActionItems)))
